import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">&ldquo;{quote}&rdquo;</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'North Lincolnshire has a RED DfT spend rating — does that guarantee my claim will succeed?',
    answer: 'No guarantee. The Department for Transport awards North Lincolnshire a RED spend scorecard alongside an overall RED rating. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on scorecard colours alone. What helps is evidence tied to your road: prior reports, photos showing defect age, and the council\'s own U-road deterioration data (green-rated U-roads falling from 72% in 2020 to 50% in 2024).',
  },
  {
    question: 'The council revised its spending figures in January 2026 — which numbers should I trust?',
    answer: 'Use the revised table published after the council\'s meeting with the DfT on 13 January 2026. The council states an original June 2025 table was misinterpreted and has been corrected — two figures were combined to reflect the value expected by the DfT. The revised figures show capital spend of £7.295m in 2024/25 against a DfT allocation of £6.537m. The original lower capital-spend rows remain on the council page for transparency but should not be cited as current spend.',
  },
  {
    question: 'What if my pothole was on an unclassified road?',
    answer: 'Unclassified roads make up 733km — 52% of North Lincolnshire\'s 1,406km carriageway network. AEI survey data shows green-rated U-roads fell from 72% in 2020 to 50% in 2024, while amber rose from 14% to 34%. That represents roughly 161km of network moving from acceptable to problematic condition. If your incident was on a U-road, the council\'s own published deterioration on the road class where most claims arise is directly relevant.',
  },
  {
    question: 'Why does the Scanner versus AEI switch matter?',
    answer: 'The council\'s 2024 condition figures for A and B/C roads were produced from independent Annual Engineers Inspections (AEI) rather than SCANNER laser surveys. B/C road RED condition rose from 4.7% in 2023 to 9.59% in 2024, and A-road RED rose from 2.6% to 4.01%. The council states it will recommence reporting via SCANNER data for 2025 and "expect to return to figures in the region of 3%" for A-roads and "in the region of 5%" for B/C roads — acknowledging the methodology change drove the trend.',
  },
  {
    question: 'North Lincolnshire filled 20,200 potholes in 2024/25 — does that mean the roads are fixed?',
    answer: 'No. The council estimates pothole fills from tarmac volumes using a standard 300mm × 300mm × 40mm definition — the same threshold it uses for investigatory repairs. Fills rose from 17,260 in 2020/21 to 20,200 in 2024/25, a 17% increase, while U-road condition continued to deteriorate and miles resurfaced fell to 21 in 2024/25. Reactive repair volume indicates ongoing defect formation; it is not proof your specific pothole was maintained to a reasonable standard.',
  },
  {
    question: 'Resurfacing fell from 45 miles to 21 miles — does that strengthen a claim?',
    answer: 'It can provide context. The council treated 45 miles in 2020/21 and 21 miles in 2024/25 across surface dressing, resurfacing and recycling programmes. On a 874-mile network (1,406km), 21 miles is roughly 2.4% — below the 5–7% annual treatment rate often cited as necessary to maintain a network. For 2025/26 the council plans around 11 miles of carriageway resurfacing plus surface dressing and recycling. Whether that gap affected your specific road depends on inspection records and defect history.',
  },
  {
    question: 'How do I report a pothole to North Lincolnshire Council?',
    answer: 'Report road and path surface problems via the council\'s online form at northlincs.gov.uk or by calling 01724 297000. The council states a qualified inspector assesses every reported defect, usually within 10 working days. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function NorthLincolnshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="North Lincolnshire Pothole Claims | U-Roads 72% to 50% Green | Fixtyer"
        description="North Lincolnshire earns RED DfT ratings overall and on spend. U-road green condition fell from 72% to 50%, pothole fills rose to 20,200 in 2024/25, and the council switched survey methods in 2024. What that means for your Section 58 claim."
        canonical="/councils/north-lincolnshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'North Lincolnshire', url: '/councils/north-lincolnshire' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="North Lincolnshire Council"
        councilSlug="north-lincolnshire"
        rating="red"
      />
      <SpeakableSchema
        url="/councils/north-lincolnshire"
        headline="North Lincolnshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">North Lincolnshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="red" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-amber-200 text-sm">
              <strong className="text-amber-400">Report caveat:</strong> North Lincolnshire updated its transparency
              report after a meeting with the DfT on 13 January 2026. Capital spend figures in an earlier table were
              misinterpreted and have been revised — see the spending section below before citing any figures.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            20,200 Pothole Repairs as U-Road Green Condition Falls from 72% to 50%
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            North Lincolnshire Council maintains 1,406km of roads and earns{' '}
            <strong className="text-rose-400">RED</strong> DfT scorecards overall and on spend, with condition rated{' '}
            <strong className="text-amber-400">AMBER</strong>. Its own transparency data shows{' '}
            <strong className="text-rose-400">733km of unclassified roads (52% of the network)</strong> where
            green-rated condition dropped 22 percentage points in four years, pothole fills rose 17% to 20,200 in
            2024/25, and a temporary switch from SCANNER to engineer inspections in 2024 doubled published RED
            figures on B/C roads. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">50%</div>
            <div className="text-white font-medium mt-2">Green-rated U-roads in 2024</div>
            <div className="text-brand-300 text-sm mt-1">
              Down from 72% in 2020 on 733km of unclassified roads — half the network now amber or red by the
              council&apos;s own AEI survey data, while the council filled an estimated 20,200 potholes in 2024/25.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,406km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from North Lincolnshire&apos;s highways maintenance transparency report — where pothole
            claims most often arise
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">1,406km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">733km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">52% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">493km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">180km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">North Lincolnshire highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">1,596km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">546km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">534km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges and structures</td>
                  <td className="px-6 py-4 text-center font-medium">475 structures (114 bridges, 349 culverts)</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">22,519</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="The highway network in North Lincolnshire comprises 1406 km of roads. With an asset value more than £1.72 billion (excluding land), it is the most valuable infrastructure asset owned by the Council."
            source="North Lincolnshire Council Highways Maintenance Transparency Report (updated January 2026)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            U-Road Condition: 72% Green to 50% Green in Four Years
          </h2>
          <p className="text-surface-600 mb-8">
            AEI survey data for unclassified roads — re-calculated from Annual Engineers Inspections, replacing
            earlier Coarse Visual Inspection submissions to the DfT
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> The council states U-road figures above were re-calculated using
              AEI survey data — previously, numbers submitted to the DfT were based on Coarse Visual Inspections (CVI).
              Classified A and B/C roads used SCANNER laser surveys until 2024, when independent AEI replaced SCANNER
              temporarily. The council plans to return to SCANNER reporting for 2025.
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (733km) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Problematic</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { year: '2020', red: '14%', amber: '14%', green: '72%', problem: '28%' },
                    { year: '2021', red: '15%', amber: '15%', green: '70%', problem: '30%' },
                    { year: '2022', red: '18%', amber: '25%', green: '57%', problem: '43%' },
                    { year: '2023', red: '16%', amber: '33%', green: '51%', problem: '49%' },
                    { year: '2024', red: '16%', amber: '34%', green: '50%', problem: '50%', highlight: true },
                  ].map((row) => (
                    <tr key={row.year} className={row.highlight ? 'bg-rose-50' : 'border-b border-amber-100'}>
                      <td className={`px-4 py-2 font-mono ${row.highlight ? 'font-bold' : ''}`}>{row.year}</td>
                      <td className="px-4 py-2 text-center">{row.red}</td>
                      <td className="px-4 py-2 text-center">{row.amber}</td>
                      <td className={`px-4 py-2 text-center ${row.highlight ? 'font-bold text-rose-600' : ''}`}>{row.green}</td>
                      <td className={`px-4 py-2 text-center ${row.highlight ? 'font-bold text-rose-600' : ''}`}>{row.problem}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Green-rated U-roads fell 22 percentage points from 2020 to 2024 — roughly 161km of network moving from
              acceptable to amber or red condition. The council states U-roads are &ldquo;performing roughly in line
              with the national average,&rdquo; but half the unclassified network is now amber or red by its own AEI
              data.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (180km) — SCANNER until 2024, then AEI</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023 (Scanner)</td>
                    <td className="px-4 py-2 text-center">2.6%</td>
                    <td className="px-4 py-2 text-center">16.1%</td>
                    <td className="px-4 py-2 text-center">81.3%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024 (AEI)</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4.01%</td>
                    <td className="px-4 py-2 text-center font-bold">18.24%</td>
                    <td className="px-4 py-2 text-center font-bold">77.75%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (493km) — RED condition more than doubled in 2024</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023 (Scanner)</td>
                    <td className="px-4 py-2 text-center">4.7%</td>
                    <td className="px-4 py-2 text-center">26.3%</td>
                    <td className="px-4 py-2 text-center">69.0%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024 (AEI)</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">9.59%</td>
                    <td className="px-4 py-2 text-center font-bold">34.14%</td>
                    <td className="px-4 py-2 text-center font-bold">56.26%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <QuoteCard
              quote="Reason for our change in trend on previous years was due to a temporary change in the data collection method. The 2024 figures were produced from an Independent Annual Engineers Inspection (AEI) as opposed to our traditional Scanner survey method. We will recommence reporting via Scanner data for 2025 when we expect to return to figures in the region of 3%."
              source="North Lincolnshire Council — A-road condition note, transparency report"
            />
          </div>

          <QuoteCard
            quote="The figures presented above were re-calculated using AEI survey data. Previously, we had submitted numbers to Dft based on Coarse Visual Inspections (CVI)."
            source="North Lincolnshire Council Highways Maintenance Transparency Report (updated January 2026)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money — Revised January 2026 Figures
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            RED spend scorecard from the DfT, with corrected capital spend published after the January 2026 DfT meeting
          </p>

          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6 mb-8">
            <p className="text-amber-200 text-sm">
              <strong className="text-amber-400">Spending caveat:</strong> Following a meeting with the DfT on 13
              January 2026, the council confirmed an earlier June 2025 spending table was misinterpreted. Two figures
              were combined to reflect the value expected by the DfT. The revised table below is the authoritative
              source. An original table with lower capital-spend figures remains on the council page for reference.
            </p>
          </div>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">North Lincolnshire highway maintenance spending — revised figures</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-rose-900/20">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">9,030</td>
                  <td className="px-4 py-3 text-center font-bold">11,030</td>
                  <td className="px-4 py-3 text-center">3,235</td>
                  <td className="px-4 py-3 text-center">77%</td>
                  <td className="px-4 py-3 text-center">23%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">6,537</td>
                  <td className="px-4 py-3 text-center font-bold">7,295</td>
                  <td className="px-4 py-3 text-center">3,235</td>
                  <td className="px-4 py-3 text-center">69%</td>
                  <td className="px-4 py-3 text-center">31%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">8,062</td>
                  <td className="px-4 py-3 text-center">8,890</td>
                  <td className="px-4 py-3 text-center">3,235</td>
                  <td className="px-4 py-3 text-center">73%</td>
                  <td className="px-4 py-3 text-center">27%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">8,574</td>
                  <td className="px-4 py-3 text-center">11,873</td>
                  <td className="px-4 py-3 text-center">3,820</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">9,075</td>
                  <td className="px-4 py-3 text-center">11,089</td>
                  <td className="px-4 py-3 text-center">2,524</td>
                  <td className="px-4 py-3 text-center">81%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">9,595</td>
                  <td className="px-4 py-3 text-center">10,785</td>
                  <td className="px-4 py-3 text-center">2,189</td>
                  <td className="px-4 py-3 text-center">83%</td>
                  <td className="px-4 py-3 text-center">17%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is RED at DfT level</h3>
              <p className="text-brand-200 text-sm">
                The Department for Transport awards North Lincolnshire a RED spend scorecard — one of 13 authorities
                with an overall RED rating in 2025/26. Revised transparency figures show capital spend meeting or
                exceeding DfT allocation in recent years, with 69–83% classified as preventative. The RED scorecard
                reflects DfT methodology applied to the authority&apos;s published data, not a simple comparison of
                two table rows.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why claims still happen</h3>
              <p className="text-brand-200 text-sm">
                The council uses capital budgets for first-time permanent pothole repairs and multi-year schemes,
                while reactive revenue budgets cover emergency repairs pending permanent fixes. Aggregate spend does
                not prove the individual pothole was known and repaired within inspection intervals — especially on
                U-roads where condition has deteriorated while pothole fills rose 17%.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            20,200 Pothole Fills in 2024/25 — Up 17% in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated fills from tarmac volumes — defects at investigatory level (40mm deep, 300mm plan dimension)
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">North Lincolnshire estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">17,260</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">15,840</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−8.2%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">15,460</td>
                  <td className="px-6 py-4 text-center text-emerald-600">−10.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">16,320</td>
                  <td className="px-6 py-4 text-center text-surface-500">−5.4%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">20,200</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+17.0%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="A pothole will be at investigatory level when it is greater than 40mm deep and its horizontal (plan) dimension is greater than 300mm in any direction."
              source="North Lincolnshire Council Highways Maintenance Transparency Report"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Up to 15,000 potholes projected for 2025/26</h3>
              <p className="text-surface-600 text-sm">
                The council estimates up to 15,000 potholes could need attention in 2025/26 depending on weather
                conditions. It plans to resurface 38.6km of carriageway in 2025/26, which it equates to repairing
                approximately 2,570 potholes — while reactive fills continue alongside preventative programmes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resurfacing */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Resurfacing: 45 Miles to 21 Miles — a 53% Decline from Peak
          </h2>
          <p className="text-surface-600 mb-8">
            Miles treated via surface dressing, carriageway resurfacing and recycling — DfT and council capital funding
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">North Lincolnshire miles of road treated 2019-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Miles treated</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Total spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Change vs peak</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2019/20</td>
                  <td className="px-4 py-3 text-center">27</td>
                  <td className="px-4 py-3 text-center">£3.19m</td>
                  <td className="px-4 py-3 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">45</td>
                  <td className="px-4 py-3 text-center">£8.14m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">Peak</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">36</td>
                  <td className="px-4 py-3 text-center">£5.06m</td>
                  <td className="px-4 py-3 text-center">−20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">33</td>
                  <td className="px-4 py-3 text-center">£5.89m</td>
                  <td className="px-4 py-3 text-center">−27%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">25</td>
                  <td className="px-4 py-3 text-center">£5.47m</td>
                  <td className="px-4 py-3 text-center">−44%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-4 py-3 font-mono font-bold">2024/25</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">21</td>
                  <td className="px-4 py-3 text-center">£3.70m</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">−53%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">874</div>
              <div className="text-sm text-surface-600">miles of roads (1,406km)</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-rose-600">21</div>
              <div className="text-sm text-rose-800">miles treated (2024/25)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-amber-600">~11</div>
              <div className="text-sm text-amber-800">miles planned resurfacing (2025/26)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">38.6km</div>
              <div className="text-sm text-surface-600">total carriageway programme (2025/26)</div>
            </div>
          </div>

          <QuoteCard
            quote="During 2025/26 North Lincolnshire Council provisional maintenance programmes aim to: Resurface around 11 miles of carriageway, investing in the region of £4,000,000."
            source="North Lincolnshire Council Highways Maintenance Transparency Report"
          />
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How North Lincolnshire says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey methods</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A and B/C roads:</strong> SCANNER laser surveys (100% A-roads annually; 100% B-roads one direction; 50% C-roads one direction)</li>
                <li>• <strong>2024 exception:</strong> independent AEI replaced SCANNER temporarily</li>
                <li>• <strong>U-roads:</strong> Annual Engineers Inspections (AEI) by independent accredited inspectors</li>
                <li>• <strong>From 2026/27:</strong> BSI PAS 2161 five-category standard required nationally</li>
                <li>• <strong>Safety inspections:</strong> all adopted roads monitored by highway inspectors; reactive inspections from public reports</li>
              </ul>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Scheme prioritisation</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council operates a Scheme Prioritisation Scoring system using AEI condition data, highway inspector
                priority sites, road hierarchy, insurance claims, collision sites, and customer enquiries.
              </p>
              <p className="text-surface-600 text-sm">
                Streets with large numbers of potholes are analysed within the prioritisation system and placed on
                preventative maintenance programmes — but reactive emergency repairs still precede permanent fixes on
                many defects.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, North Lincolnshire
              must maintain public highways. To defend a claim under{' '}
              <strong className="text-white">Section 58</strong>, it must show a reasonable system for inspecting and
              repairing the specific defect — not just publish transparency tables.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your U-road condition captured by the most recent AEI survey — and had it deteriorated from 72% to 50% green?</li>
              <li>• Did the 2024 methodology switch from SCANNER to AEI affect how your road class was recorded?</li>
              <li>• Were there prior reports (council online form, 01724 297000) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the council&apos;s inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="We will recommence reporting via Scanner data for 2025 when we expect to return figures in the region of 5%."
            source="North Lincolnshire Council — B/C road condition note, transparency report"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a RED-Rated Unitary Authority
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: North Lincolnshire invests in preventative maintenance — but published outcomes show
            deterioration on the roads where most claims arise
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council&apos;s favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Revised capital spend at or above DfT allocation in recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 69–83% preventative maintenance share over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AMBER condition scorecard — not the lowest tier
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Structured safety inspections on all adopted roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Asset management aligned to Well-Managed Highway Infrastructure code
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect the council to cite preventative spend ratios and inspection regimes. Generic neglect arguments
                alone may not succeed.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Overall RED and spend RED DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 733km of U-roads — green condition fell from 72% to 50% (2020–2024)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 20,200 pothole fills in 2024/25 — up 17% while U-roads deteriorated
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing at 21 miles in 2024/25 — down 53% from 2020/21 peak
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2024 AEI switch doubled published B/C RED from 4.7% to 9.59%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council expects SCANNER to return lower RED percentages in 2025
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against an authority with revised capital budgets and preventative ratios, your claim lives or dies on
              the <strong className="text-white">specific defect and road class</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the 72%→50% green deterioration is your strongest structural argument</li>
              <li>• Whether the council can explain the 2024 survey methodology change for your road type</li>
            </ul>
            <p className="text-white font-medium">
              Fixtyer builds exactly this case: prior-report search, photo assessment, and citations from North
              Lincolnshire&apos;s own transparency data — including the January 2026 spending correction — where it
              helps you, without overstating what the evidence proves.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a pothole to North Lincolnshire Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. The council states a qualified inspector
            assesses every reported defect, usually within 10 working days. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.northlincs.gov.uk/roads-paths-and-transport/road-and-path-maintenance/road-and-path-surface-problems/reporting-a-concern/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a road or path concern — northlincs.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in North Lincolnshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A RED-rated authority with deteriorating U-roads demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road 72%→50% deterioration argument</li>
                <li>• No prior-report search</li>
                <li>• No January 2026 spending correction context</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road deterioration documented from council AEI data</li>
                <li>✅ 20,200 pothole fills in 2024/25 cited</li>
                <li>✅ SCANNER/AEI methodology switch referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to North Lincolnshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=north-lincolnshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My North Lincolnshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.northlincs.gov.uk/site/documents/transport-and-streets/highways-maintenance-transparency-report/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              North Lincolnshire Council Highways Maintenance Transparency Report (updated January 2026)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
